import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  useProjectView,
  useProjectParticipants,
} from '@/hooks/useProjectViews';
import { useProposalPresentation } from '@/hooks/useProposalReviews';
import { useProjectCommand } from '@/hooks/useProjectCommand';
import { useAuthStore } from '@/stores/authStore';
import { Button, Spinner, Avatar } from '@/components/ui';
import { DisputeStatusBadge } from '@/components/features/disputes/DisputeStatusBadge';
import { profileImageUrl } from '@/hooks/useProfile';
import { milestoneBudget } from '@/lib/project-presentation';
import { sanitizeUrl } from '@/lib/urlSanitizer';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import { getSubmissionPresentation } from '@/api/adapter/submissionPresentations';
import {
  getSubmissionComments,
  postSubmissionComment,
  submissionCommentRequestSchema,
} from '@/api/adapter/submissionComments';
import { z } from 'zod';

const draftSchema = z.object({
  decision: z.enum(['accept', 'reject']),
  message: z.string(),
  commentId: z.string().regex(/^0x[0-9a-f]{32}$/),
  confirmed: z.boolean(),
});
export default function MilestoneReviewPage() {
  const { projectId, milestoneId } = useParams();
  const navigate = useNavigate();
  const { currency, workerRole } = useDaoConfig();
  const session = useAuthStore((s) => s.session);
  const query = useProjectView(projectId);
  const participants = useProjectParticipants(projectId ?? '');
  const project = query.data;
  const isClient = session?.accountId === project?.client;
  const milestone = project?.proposals
    .flatMap((p) => p.milestones)
    .find((m) => m.milestoneId === milestoneId);
  const submission = milestone?.submissions[milestone.submissions.length - 1];
  const isRejectedOrDisputed =
    !!submission &&
    ['Rejected', 'RejectedWithComment'].includes(submission.review.type);
  const canReview =
    isClient ||
    (session?.accountId === project?.coordinator && isRejectedOrDisputed);
  const proposal = project?.proposals.find((p) =>
    p.milestones.some((m) => m.milestoneId === milestoneId)
  );
  const descriptionQuery = useProposalPresentation(
    projectId ?? '',
    proposal?.proposalId
  );
  const description = descriptionQuery.data?.milestones.find(
    (m) => m.key === milestone?.definition.key
  )?.description;
  const presentation = useQuery({
    queryKey: [
      'submission-presentation',
      session?.principalId,
      submission?.submissionId,
    ],
    queryFn: () => getSubmissionPresentation(submission!.submissionId),
    enabled: !!submission && canReview,
  });
  const comments = useQuery({
    queryKey: [
      'submission-comments',
      session?.principalId,
      submission?.submissionId,
    ],
    queryFn: () => getSubmissionComments(submission!.submissionId),
    enabled: !!submission && canReview,
  });
  const history = useQuery({
    queryKey: [
      'submission-history',
      session?.principalId,
      milestoneId,
      submission?.submissionId,
    ],
    queryFn: async () =>
      Promise.all(
        milestone!.submissions.slice(0, -1).map(async (s, index) => ({
          version: index + 1,
          comments: await getSubmissionComments(s.submissionId),
        }))
      ),
    enabled: !!milestone && milestone.submissions.length > 1 && canReview,
  });
  const command = useProjectCommand(projectId);
  const [decision, setDecision] = useState<'accept' | 'reject' | null>(null);
  const [comment, setComment] = useState('');
  const [emailCopied, setEmailCopied] = useState(false);
  const [additionalComment, setAdditionalComment] = useState('');
  const [commentSent, setCommentSent] = useState(false);
  const [commentBusy, setCommentBusy] = useState(false);
  const [commentError, setCommentError] = useState('');
  const working = useRef(false);
  const commentRequest = useRef<{ commentId: string; message: string }>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isPending = busy || command.busy || command.blocked;
  const mutationError = error || command.error;
  const coordinator = participants.data?.coordinator;
  const coordinatorName =
    coordinator?.profiles?.worker?.name ??
    coordinator?.displayName ??
    'Project Coordinator';
  const clientName =
    participants.data?.client.profiles?.client?.name ??
    participants.data?.client.displayName ??
    'Client';
  const coordinatorEmail =
    session?.accountId === project?.client
      ? coordinator?.contactEmail
      : participants.data?.client.contactEmail;
  const developer = participants.data?.workers.find((w) =>
    milestone?.assignments.some((a) => a.worker === w.accountId)
  );
  const developerName =
    developer?.profiles?.worker?.name ??
    developer?.displayName ??
    coordinatorName;
  const person = developer ?? coordinator;
  const developerAvatar =
    person?.workerImage && person.profiles
      ? profileImageUrl(person.profiles.principalId, 'worker')
      : undefined;
  const key = `abako-delivery-review:${session?.principalId}:${submission?.submissionId}`;
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const saved = draftSchema.parse(JSON.parse(raw));
        setDecision(saved.decision);
        setComment(saved.message);
      }
    } catch {
      setError(
        'The saved review cannot be read. Check this delivery before retrying.'
      );
    }
  }, [key]);
  async function handleSubmit() {
    if (
      !project ||
      !milestone ||
      !submission ||
      !session ||
      working.current ||
      !isClient
    )
      return;
    working.current = true;
    setBusy(true);
    setError('');
    try {
      const raw = sessionStorage.getItem(key);
      const draft = raw
        ? draftSchema.parse(JSON.parse(raw))
        : {
            decision: decision!,
            message: comment.trim(),
            commentId: '0x' + crypto.randomUUID().replace(/-/g, ''),
            confirmed: false,
          };
      if (!draft.decision || (draft.decision === 'reject' && !draft.message))
        return;
      if (draft.message) {
        const validation = submissionCommentRequestSchema.safeParse({
          commentId: draft.commentId,
          message: draft.message,
          kind: draft.decision === 'reject' ? 'Rejection' : 'Comment',
        });
        if (!validation.success)
          throw new Error(
            validation.error.issues[0]?.message ?? 'Invalid comment.'
          );
      }
      sessionStorage.setItem(key, JSON.stringify(draft));
      const assertOwner = () => {
        if (
          useAuthStore.getState().session?.principalId !== session.principalId
        )
          throw new Error(
            'Your account changed. Log in again before retrying.'
          );
      };
      assertOwner();
      if (!draft.confirmed) {
        if (draft.decision === 'reject')
          await postSubmissionComment(submission.submissionId, {
            commentId: draft.commentId,
            message: draft.message,
            kind: 'Rejection',
          });
        assertOwner();
        const result = await command.run(
          command.pending
            ? null
            : draft.decision === 'accept'
              ? {
                  kind: 'completion-accept',
                  projectId: project.projectId,
                  milestoneId: milestone.milestoneId,
                  payload: { submissionId: submission.submissionId },
                }
              : {
                  kind: 'completion-reject',
                  projectId: project.projectId,
                  submissionId: submission.submissionId,
                  payload: { commentId: draft.commentId },
                }
        );
        if (!result) return;
        assertOwner();
        draft.confirmed = true;
        sessionStorage.setItem(key, JSON.stringify(draft));
      }
      if (draft.decision === 'accept' && draft.message)
        await postSubmissionComment(submission.submissionId, {
          commentId: draft.commentId,
          message: draft.message,
          kind: 'Comment',
        });
      assertOwner();
      sessionStorage.removeItem(key);
      await comments.refetch();
      navigate(`/projects/${project.projectId}`);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Review failed. Retry the saved decision.'
      );
    } finally {
      working.current = false;
      setBusy(false);
    }
  }
  async function handleAddComment() {
    if (
      !submission ||
      !session ||
      !canReview ||
      commentBusy ||
      !additionalComment.trim()
    )
      return;
    setCommentBusy(true);
    setCommentError('');
    setCommentSent(false);
    try {
      const request = commentRequest.current ?? {
        commentId: '0x' + crypto.randomUUID().replace(/-/g, ''),
        message: additionalComment.trim(),
      };
      submissionCommentRequestSchema.parse({ ...request, kind: 'Comment' });
      if (useAuthStore.getState().session?.principalId !== session.principalId)
        throw new Error('Your account changed. Reload before commenting.');
      commentRequest.current = request;
      await postSubmissionComment(submission.submissionId, {
        ...request,
        kind: 'Comment',
      });
      await comments.refetch();
      commentRequest.current = undefined;
      setAdditionalComment('');
      setCommentSent(true);
    } catch (e) {
      setCommentError(
        e instanceof Error ? e.message : 'Comment failed. Retry.'
      );
    } finally {
      setCommentBusy(false);
    }
  }
  function handleCopyEmail() {
    if (coordinatorEmail)
      void navigator.clipboard
        .writeText(coordinatorEmail)
        .then(() => setEmailCopied(true))
        .catch(() => setError('Could not copy the email address.'));
  }
  if (query.isPending)
    return (
      <div role="status" className="p-10">
        <Spinner /> Loading milestone…
      </div>
    );
  if (query.isError || !project || !milestone || !submission)
    return (
      <div role="alert" className="p-10">
        {query.error?.message ?? 'Milestone not found.'}
      </div>
    );
  if (!canReview)
    return (
      <p role="alert" className="p-10">
        Only the project client can review a pending delivery. The coordinator
        can read and comment after rejection.
      </p>
    );
  if (
    participants.isError ||
    presentation.isError ||
    comments.isError ||
    history.isError
  )
    return (
      <div className="p-10">
        <p role="alert">
          {participants.error?.message ??
            presentation.error?.message ??
            comments.error?.message ??
            history.error?.message}
        </p>
        <Button
          onClick={() => {
            void participants.refetch();
            void presentation.refetch();
            void comments.refetch();
            if (milestone.submissions.length > 1) void history.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  if (
    participants.isPending ||
    presentation.isPending ||
    comments.isPending ||
    (milestone.submissions.length > 1 && history.isPending)
  )
    return (
      <div role="status" className="p-10">
        <Spinner /> Loading delivery details…
      </div>
    );
  const discussionHistory = history.data?.some((v) => v.comments.length) && (
    <details className="rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6">
      <summary className="cursor-pointer text-sm font-semibold">
        Previous delivery reviews
      </summary>
      {history.data.map(
        (v) =>
          v.comments.length > 0 && (
            <section key={v.version} className="mt-4 space-y-3">
              <h3 className="text-sm font-semibold">
                Delivery version {v.version}
              </h3>
              {v.comments.map((c) => (
                <article
                  key={c.commentId}
                  className="rounded-xl bg-[var(--base-surface-1,#141414)] p-4 text-sm"
                >
                  <p className="font-semibold">
                    {c.authorAccount === project.client
                      ? clientName
                      : coordinatorName}
                    {c.kind === 'Rejection' ? ' · Reason for rejection' : ''}
                  </p>
                  <time className="text-xs text-[--text-dark-secondary]">
                    {new Date(c.createdAt * 1000).toLocaleString()}
                  </time>
                  <p className="mt-2 whitespace-pre-wrap break-words">
                    {c.message}
                  </p>
                </article>
              ))}
            </section>
          )
      )}
    </details>
  );
  const budgetDisplay = milestoneBudget(milestone) + ' ' + currency.symbol;
  const reason = comments.data?.find(
    (c) =>
      c.kind === 'Rejection' &&
      submission.review.type === 'RejectedWithComment' &&
      c.commentId === submission.review.commentId
  );
  if (project.activeDisputeId)
    return (
      <div className="p-10">
        <Link to={'/disputes/' + project.activeDisputeId}>
          View public dispute
        </Link>
      </div>
    );
  if (project.cancelled || milestone.frozen)
    return (
      <p role="alert" className="p-10">
        Delivery review is unavailable while the project is cancelled or frozen.
      </p>
    );
  if (isRejectedOrDisputed) {
    return (
      <div className="min-h-screen bg-[var(--base-surface-1,#141414)]">
        <div className="px-8 py-10 lg:px-[112px]">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center gap-1 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] hover:text-[var(--text-dark-primary,#f5f5f5)]"
          >
            <i className="ri-arrow-left-s-line text-lg" />
            Back to Dashboard
          </button>

          <h1 className="mb-2 text-[30px] font-bold leading-[42px] text-[var(--text-dark-primary,#f5f5f5)]">
            Review Milestone
          </h1>
          <p className="mb-8 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            Review the submitted work for{' '}
            <span className="text-[var(--state-brand-active,#36d399)]">
              {project.title}
            </span>
          </p>

          <div className="flex flex-col items-start gap-6 lg:flex-row">
            {/* Left column */}
            <div className="min-w-0 flex-1 space-y-6">
              {/* Milestone Details Card */}
              <div className="rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6">
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h2 className="mb-1 text-lg font-semibold text-[var(--text-dark-primary,#f5f5f5)]">
                      {milestone.definition.title}
                    </h2>
                    {description && (
                      <p className="text-sm leading-relaxed text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                        {description}
                      </p>
                    )}
                  </div>
                  <span className="inline-flex h-7 shrink-0 items-center rounded-[var(--radi-6,12px)] border border-[rgba(54,211,153,0.2)] bg-[rgba(54,211,153,0.12)] px-3 text-sm font-semibold text-[var(--state-brand-active,#36d399)]">
                    {budgetDisplay}
                  </span>
                </div>

                <div className="flex items-center gap-3 border-t border-[var(--base-border,#3d3d3d)] pt-4">
                  <Avatar
                    src={developerAvatar}
                    alt={developerName}
                    size="md"
                    className="h-10 w-10 border border-[var(--base-border,#3d3d3d)]"
                  />
                  <div>
                    <p className="text-sm font-medium text-[var(--text-dark-primary,#f5f5f5)]">
                      {developerName}
                    </p>
                    <p className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                      {workerRole}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dispute Status & Waiting Notice Box (Figma frame #1448:16883) */}
              <div className="space-y-4 rounded-[var(--radi-6,12px)] border border-[#6B1919] bg-[#461A1A]/60 p-6">
                <div className="flex flex-wrap items-center gap-2.5">
                  <DisputeStatusBadge
                    variant="disputed"
                    customLabel="Changes requested"
                    size="md"
                  />
                  <DisputeStatusBadge
                    variant="waiting"
                    customLabel="Review and discussion"
                    size="md"
                  />
                </div>

                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-white">
                    Discuss the requested changes with{' '}
                    {isClient ? coordinatorName : clientName}.
                  </h3>
                  <p className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                    Comments are saved for this delivery version. Requesting
                    changes does not open a dispute.
                  </p>
                </div>
              </div>

              {/* Rejection History Card */}
              <div className="space-y-3 rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6">
                <div className="flex items-center gap-2 text-sm font-bold text-rose-400">
                  <i className="ri-close-circle-fill text-lg" />
                  <span>
                    {isClient
                      ? 'You rejected the milestone'
                      : 'The client requested changes'}
                  </span>
                </div>
                <div className="rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] p-4 text-xs text-gray-200">
                  <p className="mb-1 font-semibold text-gray-400">
                    Registered Reason for Rejection:
                  </p>
                  <p className="italic leading-relaxed">
                    {reason?.message ?? 'Loading the recorded reason…'}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {comments.data
                  ?.filter((c) => c.kind === 'Comment')
                  .map((c) => (
                    <article
                      key={c.commentId}
                      className="rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6 text-left"
                    >
                      <p className="text-sm font-semibold">
                        {c.authorAccount === project.client
                          ? clientName
                          : coordinatorName}
                      </p>
                      <time className="text-xs text-[--text-dark-secondary]">
                        {new Date(c.createdAt * 1000).toLocaleString()}
                      </time>
                      <p className="mt-3 whitespace-pre-wrap break-words text-sm">
                        {c.message}
                      </p>
                    </article>
                  ))}
              </div>
              {discussionHistory}
              {commentError && <p role="alert">{commentError}</p>}
              {/* Add Comment Section (Figma frame #1448:16883) */}
              <div className="space-y-4 rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6">
                <h3 className="text-base font-semibold text-white">
                  Add Comment
                </h3>
                <textarea
                  aria-label="Additional comment"
                  maxLength={4000}
                  value={additionalComment}
                  onChange={(e) => setAdditionalComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={4}
                  className="w-full resize-none rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] px-4 py-3 text-sm text-[var(--text-dark-primary,#f5f5f5)] placeholder:text-[var(--text-dark-secondary,rgba(255,255,255,0.5))] focus:outline-none focus:ring-1 focus:ring-amber-400"
                />

                <div className="flex items-center justify-between pt-1">
                  {commentSent && (
                    <span className="flex items-center gap-1 text-xs text-emerald-400">
                      <i className="ri-checkbox-circle-line" /> Comment
                      submitted
                    </span>
                  )}
                  <div className="ml-auto">
                    <Button
                      variant="primary"
                      onClick={() => void handleAddComment()}
                      isLoading={commentBusy}
                      disabled={!additionalComment.trim() || commentBusy}
                      className="!bg-[var(--state-brand-active,#36d399)] text-xs font-semibold text-[#141414]"
                    >
                      Add Comment
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: HelpCard */}
            <div className="w-full shrink-0 space-y-4 lg:w-[420px]">
              <HelpCard
                email={coordinatorEmail}
                copied={emailCopied}
                onCopy={handleCopyEmail}
                recipient={isClient ? 'Project Leader' : 'Client'}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (
    (submission.review.type !== 'PendingReview' || !isClient) &&
    !sessionStorage.getItem(key)
  )
    return (
      <div className="p-10">
        {submission.review.type === 'Accepted'
          ? 'Delivery accepted.'
          : 'Delivery submitted for client review.'}{' '}
        <Link to={'/projects/' + project.projectId}>Back to project</Link>
        {comments.data
          ?.filter((c) => c.kind === 'Comment')
          .map((c) => (
            <article
              key={c.commentId}
              className="mt-4 rounded-xl border border-[var(--base-border,#3d3d3d)] p-4"
            >
              <p className="text-sm font-semibold">
                {c.authorAccount === project.client
                  ? clientName
                  : coordinatorName}
              </p>
              <p className="mt-2 whitespace-pre-wrap break-words">
                {c.message}
              </p>
            </article>
          ))}
        {discussionHistory}
      </div>
    );
  return (
    <div className="min-h-screen bg-[var(--base-surface-1,#141414)]">
      <div className="px-8 py-10 lg:px-[112px]">
        {/* Back navigation */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-1 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] hover:text-[var(--text-dark-primary,#f5f5f5)]"
        >
          <i className="ri-arrow-left-s-line text-lg" />
          Back to Dashboard
        </button>

        {/* Page title */}
        <h1 className="mb-2 text-[30px] font-bold leading-[42px] text-[var(--text-dark-primary,#f5f5f5)]">
          Review Milestone
        </h1>
        <p className="mb-8 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
          Review the submitted work for{' '}
          <span className="text-[var(--state-brand-active,#36d399)]">
            {project.title}
          </span>
        </p>

        {/* Two-column layout when reject panel is active */}
        <div
          className={`flex items-start gap-6 ${decision === 'reject' ? 'flex-col lg:flex-row' : 'max-w-3xl flex-col'}`}
        >
          {/* Left column: milestone details + decision card */}
          <div className={decision === 'reject' ? 'min-w-0 flex-1' : 'w-full'}>
            {/* Milestone details card */}
            <div className="mb-6 rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="mb-1 text-lg font-semibold text-[var(--text-dark-primary,#f5f5f5)]">
                    {milestone.definition.title}
                  </h2>
                  {description && (
                    <p className="text-sm leading-relaxed text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                      {description}
                    </p>
                  )}
                </div>
                <span className="inline-flex h-7 shrink-0 items-center rounded-[var(--radi-6,12px)] border border-[rgba(54,211,153,0.2)] bg-[rgba(54,211,153,0.12)] px-3 text-sm font-semibold text-[var(--state-brand-active,#36d399)]">
                  {budgetDisplay}
                </span>
              </div>

              {/* Developer info */}
              <div className="flex items-center gap-3 border-t border-[var(--base-border,#3d3d3d)] pt-4">
                <Avatar
                  src={developerAvatar}
                  alt={developerName}
                  size="md"
                  className="h-10 w-10 border border-[var(--base-border,#3d3d3d)]"
                />
                <div>
                  <p className="text-sm font-medium text-[var(--text-dark-primary,#f5f5f5)]">
                    {developerName}
                  </p>
                  <p className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                    {workerRole}
                  </p>
                </div>
              </div>

              {/* Documentation & links */}
              {(presentation.data?.documentation ||
                presentation.data?.links) && (
                <div className="mt-4 space-y-2 border-t border-[var(--base-border,#3d3d3d)] pt-4">
                  {presentation.data?.documentation && (
                    <div className="flex items-center gap-2 text-sm">
                      <i className="ri-file-text-line text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]" />
                      <a
                        href={
                          sanitizeUrl(presentation.data?.documentation) ??
                          undefined
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whitespace-pre-wrap break-words text-[var(--state-brand-active,#36d399)] hover:underline"
                      >
                        {presentation.data?.documentation}
                      </a>
                    </div>
                  )}
                  {presentation.data?.links && (
                    <div className="flex items-center gap-2 text-sm">
                      <i className="ri-link text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]" />
                      <a
                        href={
                          sanitizeUrl(presentation.data?.links) ?? undefined
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whitespace-pre-wrap break-words text-[var(--state-brand-active,#36d399)] hover:underline"
                      >
                        {presentation.data?.links}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Decision toggle */}
            <div className="mb-6 rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6">
              <h3 className="mb-4 text-sm font-semibold text-[var(--text-dark-primary,#f5f5f5)]">
                Your Decision
              </h3>

              <div className="mb-5 grid grid-cols-2 gap-3">
                <button
                  onClick={() => setDecision('accept')}
                  disabled={
                    isPending ||
                    !!command.pending ||
                    !!sessionStorage.getItem(key)
                  }
                  className={`flex h-12 items-center justify-center gap-2 rounded-[var(--radi-6,12px)] border text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                    decision === 'accept'
                      ? 'border-[var(--state-brand-active,#36d399)] bg-[var(--state-brand-active,#36d399)] text-[var(--text-light-primary,#141414)]'
                      : 'border-[var(--base-border,#3d3d3d)] bg-transparent text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] hover:border-[var(--state-brand-active,#36d399)] hover:text-[var(--text-dark-primary,#f5f5f5)]'
                  }`}
                >
                  <i className="ri-check-line text-lg" />
                  Accept Delivery
                </button>
                <button
                  onClick={() => setDecision('reject')}
                  disabled={
                    isPending ||
                    !!command.pending ||
                    !!sessionStorage.getItem(key)
                  }
                  className={`flex h-12 items-center justify-center gap-2 rounded-[var(--radi-6,12px)] border text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                    decision === 'reject'
                      ? 'border-red-500 bg-red-500 text-white'
                      : 'border-[var(--base-border,#3d3d3d)] bg-transparent text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] hover:border-red-400 hover:text-[var(--text-dark-primary,#f5f5f5)]'
                  }`}
                >
                  <i className="ri-close-line text-lg" />
                  Reject Delivery
                </button>
              </div>

              {/* Comment textarea — only shown when not rejecting (reject has its own panel) */}
              {decision !== 'reject' && (
                <div>
                  <label className="mb-2 block text-xs font-medium text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                    Comment (optional)
                  </label>
                  <textarea
                    aria-label="Review comment"
                    maxLength={4000}
                    value={comment}
                    disabled={
                      isPending ||
                      !!command.pending ||
                      !!sessionStorage.getItem(key)
                    }
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment about this delivery..."
                    rows={4}
                    className="w-full resize-none rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] px-4 py-3 text-sm text-[var(--text-dark-primary,#f5f5f5)] placeholder:text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] focus:outline-none focus:ring-1 focus:ring-[var(--state-brand-active,#36d399)]"
                  />
                </div>
              )}

              {/* Submit */}
              <Button
                variant={decision === 'reject' ? 'destructive' : 'primary'}
                onClick={() => void handleSubmit()}
                isLoading={isPending}
                disabled={
                  isPending ||
                  !decision ||
                  (decision === 'reject' && !comment.trim())
                }
                className="mt-4 w-full"
              >
                {decision === 'accept'
                  ? 'Confirm Accept'
                  : decision === 'reject'
                    ? 'Submit Response'
                    : 'Select a decision above'}
              </Button>

              {decision === 'reject' && !comment.trim() && (
                <p className="mt-2 text-center text-xs text-rose-400">
                  * Stating the reasons for rejection is required (Criterio 1)
                </p>
              )}

              {mutationError && (
                <p role="alert" className="mt-3 text-sm text-red-400">
                  {mutationError}
                </p>
              )}
            </div>

            {/* Help card — shown below decision card when not in reject mode */}
            {decision !== 'reject' && coordinatorEmail && (
              <HelpCard
                email={coordinatorEmail!}
                copied={emailCopied}
                onCopy={handleCopyEmail}
              />
            )}
            {discussionHistory}
          </div>

          {/* Right column: rejection warning panel (only shown when reject is selected) */}
          {decision === 'reject' && (
            <div className="w-full shrink-0 space-y-4 lg:w-[420px]">
              {/* Warning box */}
              <div
                className="rounded-[var(--radi-6,12px)] border p-5"
                style={{
                  backgroundColor: 'var(--colors-red-50, #461a1a)',
                  borderColor: 'var(--colors-red-100, #6b1919)',
                }}
              >
                <div className="mb-3 flex items-start gap-3">
                  <i
                    className="ri-error-warning-line mt-0.5 shrink-0 text-xl"
                    style={{ color: 'var(--state-danger-active, #ff7575)' }}
                  />
                  <h3
                    className="text-sm font-semibold leading-tight"
                    style={{ color: 'var(--state-danger-active, #ff7575)' }}
                  >
                    You're about to reject a milestone submission
                  </h3>
                </div>
                <p className="mb-4 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                  Requesting changes keeps payment in escrow and lets the
                  coordinator resubmit. It does not open a dispute.
                </p>
                <ol className="space-y-3">
                  {[
                    'You, as the client, will state your reasons, which are saved for this delivery version.',
                    'The Project Coordinator can then add their arguments and change their submission.',
                    'Until you don’t accept the milestone, the payment will be held.',
                  ].map((text, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span
                        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                        style={{
                          backgroundColor: 'var(--colors-red-100, #6b1919)',
                          color: 'var(--state-danger-active, #ff7575)',
                        }}
                      >
                        {idx + 1}
                      </span>
                      <span className="text-xs leading-relaxed text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                        {text}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Rejection reason textarea */}
              <div className="rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-5">
                <label className="mb-2 block text-xs font-medium text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                  State your reasons for rejection{' '}
                  <span className="text-rose-400">*</span>
                </label>
                <textarea
                  aria-label="Review comment"
                  maxLength={4000}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Explain why the deliverable does not meet the agreed requirements (required)..."
                  disabled={
                    isPending ||
                    !!command.pending ||
                    !!sessionStorage.getItem(key)
                  }
                  rows={5}
                  required
                  className="w-full resize-none rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] px-4 py-3 text-sm text-[var(--text-dark-primary,#f5f5f5)] placeholder:text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] focus:outline-none focus:ring-1 focus:ring-[var(--state-danger-active,#ff7575)]"
                />
              </div>

              {/* Help card within reject panel */}
              <HelpCard
                email={coordinatorEmail}
                copied={emailCopied}
                onCopy={handleCopyEmail}
                recipient={isClient ? 'Project Leader' : 'Client'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface HelpCardProps {
  email?: string | null;
  recipient?: string;
  copied: boolean;
  onCopy: () => void;
}
function HelpCard({
  email,
  copied,
  onCopy,
  recipient = 'Project Leader',
}: HelpCardProps): JSX.Element {
  return (
    <div className="rounded-[var(--radi-6,12px)] border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-5">
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--colors-emerald-200, #036347)' }}
        >
          <i className="ri-question-line text-sm text-[var(--text-dark-primary,#f5f5f5)]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-sm font-medium text-[var(--text-dark-primary,#f5f5f5)]">
            {recipient === 'Client'
              ? 'Questions about the requested changes?'
              : 'Not sure if to accept or reject?'}
          </p>
          <p className="mb-3 text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            Contact the {recipient} to further investigate
          </p>
          <div className="flex items-center gap-2">
            <span className="truncate text-sm text-[var(--state-brand-active,#36d399)]">
              {email ?? 'Contact email unavailable'}
            </span>
            <button
              onClick={onCopy}
              title="Copy email"
              aria-label="Copy email"
              disabled={!email}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radi-4,8px)] border border-[var(--base-border,#3d3d3d)] text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] transition-colors hover:border-[var(--state-brand-active,#36d399)] hover:text-[var(--text-dark-primary,#f5f5f5)]"
            >
              <i
                className={`text-xs ${copied ? 'ri-check-line text-[var(--state-brand-active,#36d399)]' : 'ri-clipboard-line'}`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
